// ---------------------------------------------------------------------------
// GUIDES — educational content. Each guide targets one clear search intent.
// All medical wording must stay responsible: traditional concepts are
// presented as tradition; no cures, guarantees or invented evidence.
// The clinic's ONLY physical location is Kurnool — never imply otherwise.
// ---------------------------------------------------------------------------
export const GUIDES = [
    {
        slug: "marma-chikitsa",
        title: "What is Marma Chikitsa? A Beginner's Guide",
        description:
            "What is Marma Chikitsa? A clear, honest introduction to the classical Ayurvedic practice of marma points — how sessions work, what tradition says, and what to expect.",
        intro:
            "Marma Chikitsa is one of the lesser-known practices of the Ayurvedic tradition. If you have heard the term and wondered what it actually involves — without the mysticism and without the marketing — this guide is for you.",
        sections: [
            {
                h: "The meaning of 'marma'",
                p: [
                    "The Sanskrit word marma is often translated as a 'vital' or 'sensitive' point. Classical Ayurvedic literature — most notably the tradition associated with the Sushruta Samhita — describes 107 marma points on the human body, located where muscles, vessels, ligaments, bones and joints are understood to meet.",
                    "'Chikitsa' simply means practice or therapy. Marma Chikitsa, then, is the practice of working with these points — traditionally with gentle, precise touch, and often with warm herbal oils.",
                ],
            },
            {
                h: "How it is practiced today",
                p: [
                    "A modern marma session is calm and unhurried. You rest comfortably while the practitioner applies light, deliberate pressure to selected points. There are no forceful manipulations. At our centre in Kurnool, every session begins with a conversation about your comfort, routine and health background before any work begins.",
                    "Sessions are often combined with elements of Abhyanga — the classical warm-oil massage tradition — depending on what suits you.",
                ],
            },
            {
                h: "How it differs from massage",
                p: [
                    "Massage works primarily with muscles, using flowing strokes, kneading and pressure across soft tissue. Marma therapy uses still, light touch held on individual traditional points — quieter and more inward than a typical massage. People usually book a massage for physical tension; they come to marma therapy for deep rest and a settled mind. Many guests enjoy both, for different reasons. Neither is a medical treatment.",
                ],
            },
            {
                h: "Traditional concepts, honestly framed",
                p: [
                    "Ayurvedic tradition describes marma points as seats of prana — vital energy. This is a traditional framework, not a modern medical one, and we present it as such. Scientific research into marma therapy specifically is still limited, and no responsible practitioner will promise you medical outcomes from it.",
                    "What most guests notice is simpler: deep rest, a settled mind, and relief from the feeling of accumulated everyday tension.",
                ],
            },
            {
                h: "Who explores Marma Chikitsa",
                p: [
                    "People come to marma therapy for many reasons: curiosity about traditional Indian wellness, stressful work routines, a desire for stillness, or as a complement to an existing wellness routine. It suits beginners — there is nothing you need to know or prepare, only health information you should share with us beforehand.",
                ],
            },
        ],
    },
    {
        slug: "marma-points",
        title: "The 107 Marma Points: Regions, Meaning and Examples",
        description:
            "The 107 marma points explained simply — where they are, how classical Ayurveda groups them by body region, and well-known examples like Sthapani, Hridaya and Nabhi marma.",
        intro:
            "Classical Ayurvedic texts describe 107 marma points distributed across the body. This guide explains how they are organised and introduces a few of the best-known points — as education, not as a self-treatment manual.",
        sections: [
            {
                h: "Why 107?",
                p: [
                    "The number comes from the classical Ayurvedic surgical tradition. The Sushruta tradition classifies 107 marma points and groups them by region: 11 in each arm and 11 in each leg (44 in the limbs), 12 across the chest and abdomen, 14 on the back, and 37 above the neck — in the head, face and throat.",
                    "Classical texts also classified points by the structures beneath them and by the traditional consequences of injury to them — one reason marma knowledge was historically guarded and taught carefully.",
                ],
            },
            {
                h: "A few well-known points",
                p: [
                    "Sthapani marma sits between the eyebrows and is traditionally associated with a settled, focused mind. Hridaya marma, at the centre of the chest, is named for the heart region. Nabhi marma at the navel is described as a centre of digestion and energy distribution. In the limbs, points such as Kshipra (the web of the hand), Manibandha (the wrist), Janu (the knee) and Talahridaya (the centre of the sole) are among the most commonly worked.",
                    "You can explore these points visually in the interactive marma points explorer on our homepage.",
                ],
            },
            {
                h: "Learning vs. self-treatment",
                p: [
                    "Reading about marma points is a wonderful way to understand the tradition. Working on them, however, is best left to a trained practitioner: classical texts themselves caution that these are sensitive locations. In a session, pressure is always light and adapted to you — nothing like a forceful massage.",
                ],
            },
            {
                h: "An honest note on evidence",
                p: [
                    "The marma system belongs to classical Ayurvedic knowledge. Modern clinical research on it is limited, and claims you may see elsewhere online — about curing diseases through marma points — are not supported and should be treated with caution. We present the tradition faithfully, without overstating it.",
                ],
            },
        ],
    },
    {
        slug: "marma-therapy-benefits",
        title: "Marma Therapy Benefits: What Tradition Says and What to Expect",
        description:
            "Marma therapy benefits explained honestly — the relaxation, balance and ease traditionally associated with marma work, what modern research does (and doesn't) say, and realistic expectations.",
        intro:
            "What does marma therapy actually do? Here is an honest answer in two parts: what the classical tradition has always said, and what you can realistically expect from a session today.",
        sections: [
            {
                h: "What the tradition says",
                p: [
                    "In classical Ayurvedic thought, marma points are junctions where the body's energies meet, and working with them is believed to settle and balance the whole system. Traditionally, marma work has been sought for rest, calm, ease of movement and general wellbeing.",
                    "These are traditional aims passed down through centuries of practice — not clinical findings.",
                ],
            },
            {
                h: "What guests commonly report",
                p: [
                    "In practice, the benefits people describe are refreshingly ordinary and human: they rest deeply during the session, leave feeling lighter, sleep better that night, and feel less wound-up by daily life. Shoulders drop. Breathing slows. Many guests build sessions into a regular routine the way others use yoga or meditation.",
                    "Experiences vary from person to person, and no outcome can be guaranteed.",
                ],
            },
            {
                h: "What modern research says",
                p: [
                    "Rigorous clinical research on marma therapy specifically is limited. Related practices — massage, relaxation therapies, meditative bodywork — have broader research suggesting benefits for stress and subjective wellbeing, but it would be wrong to claim marma therapy is scientifically proven to treat any medical condition. It is not, and we do not claim otherwise.",
                ],
            },
            {
                h: "Keeping expectations healthy",
                p: [
                    "Think of marma therapy the way you would think of a restorative wellness practice: valuable for how it makes you feel and for the space it creates in a busy life. It complements — and never replaces — the care of your physician. If you are managing a health condition, please continue your prescribed treatment and consult your doctor.",
                ],
            },
        ],
    },
    {
        slug: "marma-therapy-procedure",
        title: "Marma Therapy Procedure: What Happens in a Session, Step by Step",
        description:
            "The marma therapy procedure explained step by step — the conversation before, the gentle session itself, aftercare, and how sessions are personalized. Know exactly what to expect.",
        intro:
            "If you have never had a marma session, not knowing what will happen can be the biggest barrier. Here is exactly how a session at our Kurnool centre unfolds.",
        sections: [
            {
                h: "Step 1 — The conversation",
                p: [
                    "Every session begins with listening. We ask about what brought you in, your daily routine, and your health background. This is the moment to tell us about medical conditions, pregnancy, recent surgery or injuries — it shapes how, or whether, certain points are worked.",
                ],
            },
            {
                h: "Step 2 — Personalising your session",
                p: [
                    "There is no fixed routine done identically for everyone. Based on your conversation, the practitioner chooses the regions to work on, the pressure you are comfortable with, and whether warm herbal oils in the Abhyanga tradition are included.",
                ],
            },
            {
                h: "Step 3 — The session itself",
                p: [
                    "You rest comfortably, usually lying down, in a quiet room. The practitioner applies light, precise touch to selected marma points — unhurried, and far gentler than a deep-tissue massage. Most guests find the session deeply restful; some drift toward sleep, which is perfectly fine.",
                ],
            },
            {
                h: "Step 4 — Rest and aftercare",
                p: [
                    "Afterwards you rest for a few quiet minutes rather than jumping straight back into the day. We share simple aftercare suggestions — typically rest, water, and an easy evening. There is no complicated regimen to follow.",
                ],
            },
            {
                h: "How many sessions?",
                p: [
                    "That depends entirely on you. Some guests visit occasionally for rest; others keep a regular routine. We will never pressure you into packages or promise a fixed number of sessions will produce a specific result — responsible wellness care does not work that way.",
                ],
            },
        ],
    },
    {
        slug: "marma-therapy-safety",
        title: "Is Marma Therapy Safe? Precautions and Honest Answers",
        description:
            "Is marma therapy safe? An honest look at safety and precautions — who should consult a doctor first, what marma therapy is not, and the questions to ask any practitioner.",
        intro:
            "Safety questions deserve straight answers. Marma therapy is a gentle, non-invasive wellness practice — but 'gentle' does not mean 'no precautions'. Here is what you should know.",
        sections: [
            {
                h: "The honest baseline",
                p: [
                    "Marma Chikitsa uses light touch on traditional points. It does not involve forceful manipulation, needles, or substances you ingest, which makes it one of the gentler traditional wellness practices. For most healthy adults, a session is simply a quiet, restful experience.",
                ],
            },
            {
                h: "When to speak to your doctor first",
                p: [
                    "Please consult your physician before booking if you are pregnant, recovering from surgery or a recent injury, living with a serious or unstable medical condition, or undergoing active medical treatment. And always tell your practitioner — a good one will adapt the session or honestly advise you to wait.",
                    "Marma therapy is never appropriate as a response to a medical emergency. If you are unwell or injured, see a doctor first.",
                ],
            },
            {
                h: "What marma therapy is not",
                p: [
                    "It is not a medical treatment, and it is not a replacement for one. It cannot cure, treat or prevent disease — and any provider, anywhere, who tells you otherwise about marma therapy is not being honest with you. Be equally cautious of anyone promising guaranteed results or pressuring you to stop medication. Never stop prescribed treatment without your doctor's guidance.",
                ],
            },
            {
                h: "Questions worth asking any practitioner",
                p: [
                    "Wherever you go, ask: How do you adapt sessions for health conditions? What happens if something feels uncomfortable? What training tradition do you follow? A responsible practitioner welcomes these questions. At our centre, sessions begin with a health conversation precisely so we can answer them for you.",
                ],
            },
        ],
    },
    {
        slug: "ayurvedic-wellness",
        title: "Ayurvedic Wellness: Tradition, Daily Life and Complementary Care",
        description:
            "Ayurvedic wellness explained — daily routine, warm-oil therapies like Abhyanga and Shirodhara, and how marma knowledge fits into India's wider traditional wellness heritage.",
        intro:
            "Ayurveda is often called the science of life — a classical Indian framework for wellbeing built on routine, balance and natural therapies. Here is what that means in practice, without the exaggeration.",
        sections: [
            {
                h: "The Ayurvedic view of wellbeing",
                p: [
                    "In classical Ayurvedic thought, wellbeing comes from balance: regular daily routine, suitable food, adequate rest, and therapies matched to the individual. These are traditional concepts refined over centuries — a framework for living well rather than a system of medical treatment.",
                    "At its best, Ayurvedic wellness is deeply practical: sleep at a regular hour, eat simply, move daily, and make space for rest.",
                ],
            },
            {
                h: "Therapies in the tradition",
                p: [
                    "The classical tradition includes well-known therapies such as Abhyanga — warm-oil massage with long, rhythmic strokes — and Shirodhara, the slow pouring of warm oil over the forehead, regarded as one of Ayurveda's most calming rituals. Panchakarma describes the tradition's staged cleansing programme, always undertaken with professional guidance.",
                    "Our centre in Kurnool offers Marma Chikitsa and traditional hands-on bodywork — foot, body and head massage, cupping and scraping — within this wider tradition.",
                ],
            },
            {
                h: "A pan-Indian heritage",
                p: [
                    "Marma and Ayurvedic knowledge travelled far beyond any single text. In Kerala, the martial art Kalaripayattu developed its own deep marma tradition; in Tamil Nadu, the Siddha tradition describes a related system of vital points known as varmam. This spread across regions makes these practices a genuinely pan-Indian heritage — and readers from across India are welcome to explore our guides.",
                ],
            },
            {
                h: "Our centre in Kurnool",
                p: [
                    "Agastya Chiro and Ancient Marma Therapy is a single, independent wellness centre in Nagi Reddy Revenue Colony, near Vastralaya Mall, Kurnool, Andhra Pradesh — our only physical location. If you live nearby or are visiting Kurnool, we would be glad to welcome you; if you are elsewhere in India, we hope these guides serve you well.",
                ],
            },
        ],
    },
];

export const getGuide = (slug) => GUIDES.find((g) => g.slug === slug);
